import { useState, useRef, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  useApproveTimesheetMutation,
  useRejectTimesheetMutation,
} from '../../hooks/useTimesheetMutations';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckIcon, XMarkIcon, EyeIcon, ArrowPathIcon, ArrowUpTrayIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { StatusBadge, RejectModal } from '../../components/workflow';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { Button } from '../../components/common/Button';
import { ImportEntriesModal } from '../../components/time/ImportEntriesModal';
import {
  getPendingTimesheets,
  type TimesheetSummary,
  type TimesheetStatus,
} from '../../services/api/timeEntry';
import { getAdminUsers, getPeriods, getEmployeeTypes } from '../../services/api/admin';
import { useAuth } from '../../services/authContext';
import { formatDisplayDate } from '../../utils/format';

function formatDate(dateString: string | null): string {
  return formatDisplayDate(dateString);
}

function getUserName(user: TimesheetSummary['user']): string {
  if (!user) return 'Unknown';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
}

// Petra UAT 2026-05-20 (test A) — state-aware hours rendering. The old
// "X of Y hours" template showed "0.0 of 160.0" for Approved timesheets,
// suggesting the entries were unlogged when in fact every hour was already
// approved. Each timesheet state now formats its own headline.
function formatHoursForState(ts: TimesheetSummary): { primary: string; secondary?: string } {
  const total = ts.totalHours;
  const approvable = ts.approvableHours ?? 0;
  switch (ts.state) {
    case 'approved':
      return { primary: `${total.toFixed(1)} h approved` };
    case 'submitted':
      return {
        primary: `${approvable.toFixed(1)} h pending`,
        secondary: total > approvable ? `${total.toFixed(1)} h total` : undefined,
      };
    case 'rejected':
      return approvable > 0
        ? {
            primary: `${approvable.toFixed(1)} h to re-review`,
            secondary: total > approvable ? `${total.toFixed(1)} h total` : undefined,
          }
        : { primary: `${total.toFixed(1)} h rejected` };
    case 'open':
      return total > 0
        ? { primary: `${total.toFixed(1)} h logged` }
        : { primary: 'No entries yet' };
    default:
      return { primary: `${total.toFixed(1)} h` };
  }
}

const STATE_OPTIONS: { value: TimesheetStatus | ''; label: string }[] = [
  { value: '', label: '- All -' },
  { value: 'open', label: 'Open' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export function PendingTimesheetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useAuth();

  const [showAllProjects, setShowAllProjects] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState<TimesheetSummary | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Petra UAT 2026-05-20 (test A) — default to "All" so admin landing on
  // /manager/timesheets/pending via the sidebar entry sees the full timesheet
  // ledger (matches the /admin/timesheets/pending fix in 8494a252). The
  // previous default 'submitted' showed an empty list whenever nothing was
  // awaiting approval — indistinguishable from "no data exists".
  const filters = {
    userId: searchParams.get('userId') || '',
    employeeTypeId: searchParams.get('employeeTypeId') || '',
    state: (searchParams.has('state') ? searchParams.get('state') || '' : '') as TimesheetStatus | '',
    periodId: searchParams.get('periodId') || '',
  };

  const setFilters = (newFilters: typeof filters) => {
    const params = new URLSearchParams();
    if (newFilters.userId) params.set('userId', newFilters.userId);
    if (newFilters.employeeTypeId) params.set('employeeTypeId', newFilters.employeeTypeId);
    params.set('state', newFilters.state);
    if (newFilters.periodId) params.set('periodId', newFilters.periodId);
    setSearchParams(params, { replace: true });
  };

  // Fetch filter options
  const { data: users = [] } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => getAdminUsers(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: periods = [] } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: employeeTypes = [] } = useQuery({
    queryKey: ['employeeTypes'],
    queryFn: () => getEmployeeTypes(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch timesheets with filters
  const {
    data: timesheetsData,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['pendingTimesheets', filters, showAllProjects],
    queryFn: () =>
      getPendingTimesheets(
        // Empty state = "- All -". Send the 'all' sentinel so an admin sees
        // every state (the /pending endpoint otherwise defaults to 'submitted').
        // For a manager the backend ignores it and keeps the approval queue.
        filters.state || 'all',
        filters.periodId ? parseInt(filters.periodId, 10) : undefined,
        filters.userId ? parseInt(filters.userId, 10) : undefined,
        // Admin-only project-scope opt-in. Non-admins never set this and the
        // backend re-checks isAdmin, so scope=all from a non-admin is ignored.
        isAdmin && showAllProjects ? 'all' : undefined
      ),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

  const timesheets = timesheetsData?.data || [];

  const hasLoadedOnce = useRef(false);
  if (timesheetsData) {
    hasLoadedOnce.current = true;
  }

  // Employee type filter (client-side)
  const userEmployeeTypeMap = new Map(
    users.map((u) => [u.id, u.employeeTypeId])
  );

  const filteredTimesheets = timesheets.filter((ts) => {
    if (filters.employeeTypeId && ts.user?.id) {
      const userEmployeeType = userEmployeeTypeMap.get(ts.user.id);
      if (userEmployeeType !== parseInt(filters.employeeTypeId, 10)) {
        return false;
      }
    }
    return true;
  });

  // Period sort: 'desc' (newest first) by default
  const [periodSort, setPeriodSort] = useState<'asc' | 'desc'>('desc');

  const sortedTimesheets = useMemo(() => {
    return [...filteredTimesheets].sort((a, b) => {
      const aName = a.period?.name || '';
      const bName = b.period?.name || '';
      return periodSort === 'asc'
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    });
  }, [filteredTimesheets, periodSort]);

  const handleReset = () => {
    setSearchParams({}, { replace: true });
  };

  // Phase 28 D1 — centralised optimistic + invalidate hooks (Plan 28-05).
  // Fixes MKS-109 (approved row didn't disappear) and MKS-112 (no auto-refresh)
  // by flipping the cached state on mutate and invalidating the 6 affected
  // query keys on settle.
  const approveMutation = useApproveTimesheetMutation({
    onSuccessToast: () => {
      setShowApproveModal(false);
      setSelectedTimesheet(null);
    },
  });

  const rejectMutation = useRejectTimesheetMutation({
    onSuccessToast: () => {
      setShowRejectModal(false);
      setSelectedTimesheet(null);
    },
  });

  const handleApproveClick = (timesheet: TimesheetSummary) => {
    setSelectedTimesheet(timesheet);
    setShowApproveModal(true);
  };

  const handleRejectClick = (timesheet: TimesheetSummary) => {
    setSelectedTimesheet(timesheet);
    setShowRejectModal(true);
  };

  const handleApprove = () => {
    if (selectedTimesheet) {
      approveMutation.mutate({ id: selectedTimesheet.id, managerOnly: true });
    }
  };

  const handleReject = (reason: string) => {
    if (selectedTimesheet) {
      rejectMutation.mutate({ id: selectedTimesheet.id, reason, managerOnly: true });
    }
  };

  const showFullLoading = isFetching && !hasLoadedOnce.current;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Timesheets</h1>
          {isFetching && hasLoadedOnce.current && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              <span>Updating...</span>
            </div>
          )}
          <div className="ml-auto">
            <Button variant="secondary" onClick={() => setShowImportModal(true)}>
              <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
              Import
            </Button>
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          View and manage timesheets on your projects
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User (optional)
            </label>
            <select
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">- All -</option>
              {[...users]
                .sort((a, b) => {
                  const aName = a.lastName || a.email;
                  const bName = b.lastName || b.email;
                  return aName.localeCompare(bName);
                })
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName && user.lastName
                      ? `${user.lastName}, ${user.firstName}`
                      : user.email}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              With employee type (optional)
            </label>
            <select
              value={filters.employeeTypeId}
              onChange={(e) => setFilters({ ...filters, employeeTypeId: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">- All -</option>
              {employeeTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State (optional)
            </label>
            <select
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value as TimesheetStatus | '' })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              {STATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Period (optional)
            </label>
            <select
              value={filters.periodId}
              onChange={(e) => setFilters({ ...filters, periodId: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">- All -</option>
              {periods.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <Button variant="secondary" onClick={handleReset}>
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Reset
            </Button>
            {isAdmin && (
              <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAllProjects}
                  onChange={(e) => setShowAllProjects(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Show all projects
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      {showFullLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load timesheets. Please try again.</p>
        </div>
      ) : sortedTimesheets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No timesheets found matching your criteria.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => setPeriodSort(periodSort === 'desc' ? 'asc' : 'desc')}
                    className="inline-flex items-center gap-1 hover:text-gray-700"
                  >
                    Period
                    {periodSort === 'desc' ? (
                      <ChevronDownIcon className="h-3 w-3" />
                    ) : (
                      <ChevronUpIcon className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedTimesheets.map((timesheet) => (
                <tr key={timesheet.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <span className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                          {timesheet.user?.firstName?.[0] || timesheet.user?.email[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {getUserName(timesheet.user)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {timesheet.user?.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{timesheet.period?.name || '-'}</div>
                    <div className="text-xs text-gray-500">
                      {formatDate(timesheet.period?.from ?? null)} - {formatDate(timesheet.period?.to ?? null)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const hours = formatHoursForState(timesheet);
                      return (
                        <>
                          <div className="text-sm font-medium text-gray-900">{hours.primary}</div>
                          {hours.secondary && (
                            <div className="text-xs text-gray-500">{hours.secondary}</div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {timesheet.submittedAt ? formatDate(timesheet.submittedAt) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={timesheet.state} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/manager/timesheets/${timesheet.id}`}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="View details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      {/* Gate on state, not approvableHours: a submitted timesheet whose
                          entries are all already approved (0 h approvable) must still be
                          approvable/rejectable here, or it is stuck in Pending Review with
                          no affordance anywhere — the backend guard is state === 'submitted'
                          and the admin pending page already gates the same way. */}
                      {timesheet.state === 'submitted' && (
                        <>
                          <button
                            onClick={() => handleApproveClick(timesheet)}
                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg"
                            title="Approve"
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleRejectClick(timesheet)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                            title="Reject"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setSelectedTimesheet(null);
        }}
        onConfirm={handleApprove}
        title="Approve Timesheet"
        message={`Are you sure you want to approve ${getUserName(selectedTimesheet?.user ?? null)}'s timesheet for ${selectedTimesheet?.period?.name}?`}
        confirmText="Approve"
        variant="primary"
        // Phase 28 / review M-02: gate by EITHER pending so a user can't fire
        // a second mutation while a first is mid-flight (would corrupt the
        // rollback baseline if the first errored).
        isLoading={approveMutation.isPending || rejectMutation.isPending}
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedTimesheet(null);
        }}
        onConfirm={handleReject}
        isLoading={approveMutation.isPending || rejectMutation.isPending}
        employeeName={getUserName(selectedTimesheet?.user ?? null)}
      />

      <ImportEntriesModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} />
    </div>
  );
}
